// pages/Schedule.js — day-grid schedule (/schedule): aircraft + CFI rows, reservation chips,
// pending-request inbox, new-reservation modal, CFI weekly availability, schedulable toggle.
// Helpers passed in as props (same pattern as Dispatch).
import React, { useState, useEffect, useCallback } from 'react';

const HOURS_START = 6, HOURS_END = 22; // displayed window, school-local
const DAY_MS = 86400000;
const dow_names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dstr(d) { return d.toISOString().slice(0, 10); }
function fmtTime(iso) { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
function minToHHMM(m) { return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; }

// Maintenance-forecast warnings (server: api/maint_forecast.py). Warnings never block a booking.
const WLVL = {
  overdue: { label: 'Overdue', fg: '#991b1b', bg: '#fee2e2' },
  due: { label: 'Due before', fg: '#991b1b', bg: '#fee2e2' },
  likely: { label: 'Likely due', fg: '#9a3412', bg: '#ffedd5' },
  possible: { label: 'Possible', fg: '#854d0e', bg: '#fef9c3' },
  error: { label: 'Forecast', fg: '#475569', bg: '#f1f5f9' },
};
const worst = (ws) => (ws || []).find(w => w.level === 'overdue' || w.level === 'due') ? 'due'
  : (ws || []).find(w => w.level === 'likely') ? 'likely' : (ws || []).find(w => w.level === 'possible') ? 'possible' : null;

export function MaintWarnings({ warnings, compact }) {
  if (!warnings || !warnings.length) return null;
  return (
    <div style={{ display: 'grid', gap: 4, margin: compact ? '4px 0' : '8px 0' }}>
      {warnings.map((w, i) => {
        const c = WLVL[w.level] || WLVL.error;
        return (
          <div key={i} style={{ background: c.bg, color: c.fg, borderRadius: 6, padding: '5px 8px', fontSize: 12 }}>
            <b>🔧 {c.label}{w.grounding ? '' : ' (advisory)'}:</b> {w.msg}
          </div>
        );
      })}
    </div>
  );
}

function Chip({ r, onClick }) {
  const day0 = new Date(new Date(r.start_at).toDateString()).getTime();
  const s = Math.max((new Date(r.start_at) - day0) / 3600000, HOURS_START);
  const e = Math.min((new Date(r.end_at) - day0) / 3600000, HOURS_END);
  const left = ((s - HOURS_START) / (HOURS_END - HOURS_START)) * 100;
  const width = Math.max(((e - s) / (HOURS_END - HOURS_START)) * 100, 2);
  const colors = { flight: '#2563eb', maintenance: '#7c3aed', block: '#475569', cfi_block: '#475569' };
  const bg = r.status === 'requested' ? '#d97706' : colors[r.kind] || '#2563eb';
  const mw = worst(r.maint_warnings);
  const ring = mw === 'due' ? '#dc2626' : mw === 'likely' ? '#f97316' : mw === 'possible' ? '#eab308' : null;
  return (
    <div onClick={onClick} title={`${fmtTime(r.start_at)}–${fmtTime(r.end_at)} ${r.title || r.kind}${(r.maint_warnings || []).map(w => `\n🔧 ${w.msg}`).join('')}`}
      style={{ position: 'absolute', left: `${left}%`, width: `${width}%`, top: 4, bottom: 4,
               background: bg + (r.status === 'requested' ? 'cc' : ''), borderRadius: 6, color: '#fff',
               fontSize: 11, padding: '2px 6px', overflow: 'hidden', whiteSpace: 'nowrap', cursor: 'pointer',
               border: r.status === 'requested' ? '2px dashed #fff' : 'none',
               boxShadow: ring ? `0 0 0 2px ${ring}` : 'none' }}>
      {mw ? '🔧 ' : ''}{r.status === 'requested' ? '? ' : ''}{r.pilot_name || r.title || r.kind}{r.cfi_name ? ` + ${r.cfi_name}` : ''}
    </div>
  );
}

export function SchedulePage({ apiFetch, apiPost, apiPut, useAuth }) {
  const { token, user } = useAuth();
  const [day, setDay] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('date');
    return q ? new Date(q + 'T00:00:00') : new Date(new Date().toDateString());
  });
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(() => {   // {mode:'new', fleet_id?, hour?, kind?, title?} | {mode:'view', r} | {mode:'avail', cfi}
    const q = new URLSearchParams(window.location.search);   // deep link from the Fleet maintenance forecast
    return q.get('fleet') ? { mode: 'new', fleet_id: Number(q.get('fleet')), hour: 8, kind: q.get('kind') || 'flight', title: q.get('title') || '', allDay: q.get('kind') === 'maintenance' } : null;
  });
  const [err, setErr] = useState(null);
  useEffect(() => {   // consume the deep-link params so a reload doesn't reopen the form
    if (window.location.search) window.history.replaceState(window.history.state, '', window.location.pathname);
  }, []);

  const load = useCallback(() => {
    const start = new Date(day).toISOString(); const end = new Date(day.getTime() + DAY_MS).toISOString();
    apiFetch(`/api/schedule?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`, token).then(setData).catch(e => setErr(e.message));
  }, [day, token, apiFetch]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="page"><div className="page-header"><h1>Schedule</h1></div><p style={{ color: '#888' }}>{err || 'Loading…'}</p></div>;

  const resFor = (pred) => (data.reservations || []).filter(pred);
  const pending = resFor(r => r.status === 'requested');
  const hours = []; for (let h = HOURS_START; h < HOURS_END; h += 2) hours.push(h);

  const act = async (fn) => { try { await fn(); setModal(null); setErr(null); load(); } catch (e) { setErr(e.message); } };

  const Row = ({ label, sub, children, onNew, right }) => (
    <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid #e6eaf0', minHeight: 44 }}>
      <div style={{ width: 150, padding: '8px 10px', fontSize: 13, fontWeight: 600, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <span>{label} {right}</span>{sub && <span style={{ fontSize: 11, color: '#889', fontWeight: 400 }}>{sub}</span>}
      </div>
      <div style={{ flex: 1, position: 'relative', cursor: onNew ? 'crosshair' : 'default' }}
        onDoubleClick={onNew ? (e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const frac = (e.clientX - rect.left) / rect.width;
          const hour = Math.floor(HOURS_START + frac * (HOURS_END - HOURS_START));
          onNew(hour);
        } : undefined}>
        {hours.map(h => <div key={h} style={{ position: 'absolute', left: `${((h - HOURS_START) / (HOURS_END - HOURS_START)) * 100}%`, top: 0, bottom: 0, borderLeft: '1px solid #eef1f6' }} />)}
        {children}
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1>Schedule</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-sm" onClick={() => setDay(new Date(day.getTime() - DAY_MS))}>←</button>
          <input type="date" value={dstr(day)} onChange={e => setDay(new Date(e.target.value + 'T00:00:00'))} />
          <button className="btn btn-sm" onClick={() => setDay(new Date(day.getTime() + DAY_MS))}>→</button>
          <button className="btn btn-sm" onClick={() => setDay(new Date(new Date().toDateString()))}>Today</button>
          <button className="btn btn-primary" onClick={() => setModal({ mode: 'new' })}>+ Reservation</button>
        </div>
      </div>
      {err && <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p>}

      {pending.length > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 14, borderLeft: '4px solid #d97706' }}>
          <b style={{ fontSize: 13 }}>Requests awaiting a decision</b>
          {pending.map(r => (
            <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, marginTop: 6, flexWrap: 'wrap' }}>
              <span>{r.pilot_name || 'App user'} — {r.tail_number || 'any aircraft'}{r.cfi_name ? ` with ${r.cfi_name}` : ''} · {fmtTime(r.start_at)}–{fmtTime(r.end_at)}{r.notes ? ` · “${r.notes}”` : ''}</span>
              <button className="btn btn-sm btn-primary" onClick={() => act(() => apiPost(`/api/schedule/${r.id}/approve`, {}, token))}>Approve</button>
              <button className="btn btn-sm" onClick={() => act(() => apiPost(`/api/schedule/${r.id}/deny`, {}, token))}>Deny</button>
              {r.maint_warnings?.length > 0 && <div style={{ flexBasis: '100%' }}><MaintWarnings warnings={r.maint_warnings} compact /></div>}
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '2px solid #dfe4ec', background: '#f7f9fc' }}>
          <div style={{ width: 150 }} />
          <div style={{ flex: 1, position: 'relative', height: 22 }}>
            {hours.map(h => <span key={h} style={{ position: 'absolute', left: `${((h - HOURS_START) / (HOURS_END - HOURS_START)) * 100}%`, fontSize: 10, color: '#889', paddingLeft: 3 }}>{h}:00</span>)}
          </div>
        </div>
        {(data.fleet || []).map(f => {
          const mx = (data.maint || {})[f.id] || (data.maint || {})[String(f.id)];
          const dayW = mx?.warnings || [];
          const dw = worst(dayW);
          return (
          <Row key={f.id} label={f.tail_number}
            sub={dw ? <span title={dayW.map(w => w.msg).join('\n')} style={{ color: WLVL[dw === 'due' ? 'due' : dw].fg, fontWeight: 700, cursor: 'help' }}>
                   🔧 {dw === 'due' ? 'Maintenance due' : dw === 'likely' ? 'Mx likely due' : 'Mx possible'}</span> : f.ac_type}
            right={(user?.role === 'owner' || user?.role === 'admin') && (
              <span onClick={() => act(() => apiPost(`/api/schedule/fleet/${f.id}/schedulable`, { schedulable: !f.schedulable }, token))}
                title={f.schedulable ? 'On the schedule — click to remove' : 'Off the schedule — click to restore'}
                style={{ cursor: 'pointer', fontSize: 10, padding: '1px 6px', borderRadius: 8, marginLeft: 4,
                         background: f.schedulable ? '#dcfce7' : '#e2e8f0', color: f.schedulable ? '#16a34a' : '#64748b' }}>
                {f.schedulable ? 'ON' : 'OFF'}
              </span>
            )}
            onNew={(hour) => setModal({ mode: 'new', fleet_id: f.id, hour })}>
            {!f.schedulable && <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 8px,#e9edf3 8px,#e9edf3 16px)' }} />}
            {f.schedulable && dw && dw !== 'possible' && <div title={dayW.map(w => w.msg).join('\n')} style={{ position: 'absolute', inset: 0, background: dw === 'due' ? 'rgba(254,226,226,0.55)' : 'rgba(255,237,213,0.55)' }} />}
            {resFor(r => r.fleet_id === f.id).map(r => <Chip key={r.id} r={r} onClick={() => setModal({ mode: 'view', r })} />)}
          </Row>
          );
        })}
        <div style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#667', background: '#f7f9fc', borderTop: '2px solid #dfe4ec' }}>INSTRUCTORS</div>
        {(data.cfis || []).map(c => {
          const wins = (data.cfi_availability || []).filter(w => w.cfi_link_id === c.id && w.dow === day.getDay());
          return (
            <Row key={c.id} label={c.name}
              sub={wins.length ? wins.map(w => `${minToHHMM(w.start_min)}–${minToHHMM(w.end_min)}`).join(', ') : 'no availability set'}
              right={(user?.role === 'owner' || user?.role === 'admin') && (
                <span onClick={() => setModal({ mode: 'avail', cfi: c })} style={{ cursor: 'pointer', fontSize: 11 }} title="Edit weekly availability">🗓</span>
              )}>
              {wins.map((w, i) => (
                <div key={i} style={{ position: 'absolute', top: 4, bottom: 4,
                  left: `${Math.max(((w.start_min / 60 - HOURS_START) / (HOURS_END - HOURS_START)) * 100, 0)}%`,
                  width: `${((w.end_min - w.start_min) / 60 / (HOURS_END - HOURS_START)) * 100}%`,
                  background: '#dcfce7', borderRadius: 6 }} />
              ))}
              {resFor(r => r.cfi_link_id === c.id).map(r => <Chip key={r.id} r={r} onClick={() => setModal({ mode: 'view', r })} />)}
            </Row>
          );
        })}
      </div>
      <p style={{ fontSize: 11, color: '#889', marginTop: 8 }}>Double-click an aircraft row to create a reservation at that time. Green = CFI availability; dashed amber = pending request; striped = off the schedule. 🔧 = maintenance forecast says the aircraft is likely (orange) or already (red) due by then — hover for details; a maintenance hold before the flight clears it.</p>

      {modal?.mode === 'new' && <NewResModal data={data} init={modal} day={day} onClose={() => setModal(null)} apiFetch={apiFetch} token={token}
        onSave={(body) => act(() => apiPost('/api/schedule', body, token))} />}
      {modal?.mode === 'view' && <ViewResModal r={modal.r} onClose={() => setModal(null)}
        onCancel={() => act(() => apiPut(`/api/schedule/${modal.r.id}`, { status: 'cancelled' }, token))}
        onApprove={() => act(() => apiPost(`/api/schedule/${modal.r.id}/approve`, {}, token))}
        onDeny={() => act(() => apiPost(`/api/schedule/${modal.r.id}/deny`, {}, token))} />}
      {modal?.mode === 'avail' && <AvailModal cfi={modal.cfi} token={token} apiFetch={apiFetch} apiPut={apiPut}
        onClose={() => { setModal(null); load(); }} />}
    </div>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: 460, maxWidth: '94vw', maxHeight: '86vh', overflow: 'auto', padding: 18 }}>{children}</div>
    </div>
  );
}

function NewResModal({ data, init, day, onClose, onSave, apiFetch, token }) {
  const h = init.hour ?? 9;
  const endH = init.allDay ? 17 : h + 2;
  const [f, setF] = useState({ kind: init.kind || 'flight', fleet_id: init.fleet_id || '', cfi_link_id: '', pilot_link_id: '',
    date: dstr(day), start: `${String(h).padStart(2, '0')}:00`, end: `${String(endH).padStart(2, '0')}:00`, title: init.title || '', notes: '' });
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const [warn, setWarn] = useState([]);
  useEffect(() => {
    if (f.kind !== 'flight' || !f.fleet_id || !f.date || !f.start || !f.end) { setWarn([]); return; }
    const t = setTimeout(() => {
      apiFetch(`/api/schedule/maint-check?fleet_id=${f.fleet_id}&start_at=${encodeURIComponent(`${f.date}T${f.start}:00`)}&end_at=${encodeURIComponent(`${f.date}T${f.end}:00`)}`, token)
        .then(d => setWarn(d.warnings || [])).catch(() => setWarn([]));
    }, 300);
    return () => clearTimeout(t);
  }, [f.kind, f.fleet_id, f.date, f.start, f.end, apiFetch, token]);
  const save = () => onSave({
    kind: f.kind, fleet_id: f.fleet_id ? Number(f.fleet_id) : null, cfi_link_id: f.cfi_link_id ? Number(f.cfi_link_id) : null,
    pilot_link_id: f.pilot_link_id ? Number(f.pilot_link_id) : null,
    start_at: `${f.date}T${f.start}:00`, end_at: `${f.date}T${f.end}:00`,
    title: f.title || (f.kind !== 'flight' ? f.kind : null) || null, notes: f.notes || null,
  });
  return (
    <Overlay onClose={onClose}>
      <h3 style={{ marginTop: 0 }}>New reservation</h3>
      <div style={{ display: 'grid', gap: 8 }}>
        <select value={f.kind} onChange={set('kind')}>
          <option value="flight">Flight</option><option value="maintenance">Maintenance hold</option>
          <option value="block">Aircraft block (unavailable)</option><option value="cfi_block">CFI time off</option>
        </select>
        {f.kind !== 'cfi_block' && (
          <select value={f.fleet_id} onChange={set('fleet_id')}>
            <option value="">— aircraft —</option>
            {(data.fleet || []).map(a => <option key={a.id} value={a.id}>{a.tail_number}</option>)}
          </select>
        )}
        {f.kind === 'flight' && (
          <select value={f.pilot_link_id} onChange={set('pilot_link_id')}>
            <option value="">— pilot / student / renter —</option>
            {(data.pilots || []).map(p => <option key={p.id} value={p.id}>{p.name}{p.role !== 'pilot' ? ` (${p.role})` : ''}</option>)}
          </select>
        )}
        {(f.kind === 'flight' || f.kind === 'cfi_block') && (
          <select value={f.cfi_link_id} onChange={set('cfi_link_id')}>
            <option value="">— {f.kind === 'cfi_block' ? 'CFI' : 'CFI (optional)'} —</option>
            {(data.cfis || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="date" value={f.date} onChange={set('date')} />
          <input type="time" value={f.start} onChange={set('start')} />
          <input type="time" value={f.end} onChange={set('end')} />
        </div>
        <input placeholder="Title (shown on the chip)" value={f.title} onChange={set('title')} />
        <input placeholder="Notes" value={f.notes} onChange={set('notes')} />
        <MaintWarnings warnings={warn} />
        {f.kind === 'maintenance' && <p style={{ fontSize: 11, color: '#667', margin: 0 }}>Tip: keep the item name in the title (e.g. "100-hour inspection") — flights after this hold stop warning about that item.</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>{warn.some(w => w.level !== 'error') ? 'Create anyway' : 'Create'}</button>
        </div>
      </div>
    </Overlay>
  );
}

function ViewResModal({ r, onClose, onCancel, onApprove, onDeny }) {
  return (
    <Overlay onClose={onClose}>
      <h3 style={{ marginTop: 0 }}>{r.title || (r.kind === 'flight' ? 'Flight' : r.kind)}</h3>
      <p style={{ fontSize: 13, margin: '4px 0' }}>
        {r.tail_number || '—'}{r.cfi_name ? ` · CFI ${r.cfi_name}` : ''}{r.pilot_name ? ` · ${r.pilot_name}` : ''}<br />
        {new Date(r.start_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} → {fmtTime(r.end_at)}<br />
        Status: <b>{r.status}</b>{r.notes ? <><br />“{r.notes}”</> : null}
      </p>
      <MaintWarnings warnings={r.maint_warnings} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        {r.status === 'requested' && <><button className="btn btn-primary" onClick={onApprove}>Approve</button>
          <button className="btn" onClick={onDeny}>Deny</button></>}
        {r.status === 'confirmed' && <button className="btn" onClick={onCancel}>Cancel reservation</button>}
        <button className="btn" onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

function AvailModal({ cfi, token, apiFetch, apiPut, onClose }) {
  const [wins, setWins] = useState(null);
  useEffect(() => { apiFetch(`/api/schedule/cfi-availability/${cfi.id}`, token).then(d => setWins(d.windows || [])); }, [cfi.id, token, apiFetch]);
  if (!wins) return null;
  const save = async (next) => { setWins(next); await apiPut(`/api/schedule/cfi-availability/${cfi.id}`, { windows: next }, token); };
  return (
    <Overlay onClose={onClose}>
      <h3 style={{ marginTop: 0 }}>{cfi.name} — weekly availability</h3>
      <p style={{ fontSize: 12, color: '#667' }}>Times students can request this instructor. Repeats weekly.</p>
      {wins.map((w, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <select value={w.dow} onChange={e => save(wins.map((x, j) => j === i ? { ...x, dow: Number(e.target.value) } : x))}>
            {dow_names.map((n, d) => <option key={d} value={d}>{n}</option>)}
          </select>
          <input type="time" value={minToHHMM(w.start_min)} onChange={e => { const [hh, mm] = e.target.value.split(':'); save(wins.map((x, j) => j === i ? { ...x, start_min: Number(hh) * 60 + Number(mm) } : x)); }} />
          <input type="time" value={minToHHMM(w.end_min)} onChange={e => { const [hh, mm] = e.target.value.split(':'); save(wins.map((x, j) => j === i ? { ...x, end_min: Number(hh) * 60 + Number(mm) } : x)); }} />
          <span style={{ cursor: 'pointer', color: '#889' }} onClick={() => save(wins.filter((_, j) => j !== i))}>✕</span>
        </div>
      ))}
      <button className="btn btn-sm" onClick={() => save([...wins, { dow: 1, start_min: 540, end_min: 1020 }])}>+ window</button>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button className="btn btn-primary" onClick={onClose}>Done</button></div>
    </Overlay>
  );
}
